/**
 * The API client. The access token lives only in memory; the refresh token is
 * an httpOnly cookie on /auth, traded for a new access token when needed.
 * The active store goes in X-Varejoon-Tenant, kept per tab (sessionStorage).
 */

export interface ErrorDetail {
  path: (string | number)[];
  message: string;
}

export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly code: string,
    message: string,
    readonly details: ErrorDetail[] = [],
  ) {
    super(message);
  }
}

let accessToken: string | null = null;
let refreshing: Promise<boolean> | null = null;
const TENANT_KEY = "vj_tenant";

export function activeTenant(): string | null {
  try {
    return sessionStorage.getItem(TENANT_KEY);
  } catch {
    return null;
  }
}

export function setActiveTenant(id: string | null): void {
  try {
    if (id) sessionStorage.setItem(TENANT_KEY, id);
    else sessionStorage.removeItem(TENANT_KEY);
  } catch {
    /* private mode: the choice lasts until reload */
  }
}

export function setAccessToken(t: string | null): void {
  accessToken = t;
}

/** Trades the refresh cookie for an access token. One request at a time. */
export function refreshSession(): Promise<boolean> {
  refreshing ??= fetch("/auth/refresh", { method: "POST", credentials: "same-origin" })
    .then(async (r) => {
      if (!r.ok) return false;
      accessToken = ((await r.json()) as { accessToken: string }).accessToken;
      return true;
    })
    .catch(() => false)
    .finally(() => {
      refreshing = null;
    });
  return refreshing;
}

async function send(method: string, path: string, body: unknown, tenant: boolean): Promise<Response> {
  const headers: Record<string, string> = {};
  if (accessToken) headers.authorization = `Bearer ${accessToken}`;
  const t = activeTenant();
  if (tenant && t) headers["x-varejoon-tenant"] = t;
  if (body !== undefined) headers["content-type"] = "application/json";
  return fetch(path, { method, headers, body: body === undefined ? undefined : JSON.stringify(body), credentials: "same-origin" });
}

export async function api<T = unknown>(method: string, path: string, body?: unknown, opts: { tenant?: boolean } = {}): Promise<T> {
  const tenant = opts.tenant ?? true;
  let res = await send(method, path, body, tenant);
  if (res.status === 401 && !path.startsWith("/auth/") && (await refreshSession())) res = await send(method, path, body, tenant);
  if (res.status === 204) return undefined as T;
  const text = await res.text();
  const data = text ? (JSON.parse(text) as unknown) : null;
  if (!res.ok) {
    const e = (data as { error?: { code: string; message: string; details?: ErrorDetail[] } } | null)?.error;
    throw new ApiError(res.status, e?.code ?? "internal", e?.message ?? `Erro ${res.status}`, e?.details ?? []);
  }
  return data as T;
}

export interface FileRef {
  id: string;
  name: string;
  mime: string;
  size: number;
}

/** Sends one file to an attachment field. The id it returns goes in the record. */
export async function uploadFile(collection: string, field: string, file: File): Promise<FileRef> {
  const go = () => {
    const headers: Record<string, string> = { "content-type": file.type || "application/octet-stream", "x-file-name": encodeURIComponent(file.name) };
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    const t = activeTenant();
    if (t) headers["x-varejoon-tenant"] = t;
    return fetch(`/v1/files${qs({ collection, field })}`, { method: "POST", headers, body: file, credentials: "same-origin" });
  };
  let res = await go();
  if (res.status === 401 && (await refreshSession())) res = await go();
  const data = (await res.json().catch(() => null)) as { file?: FileRef; error?: { code: string; message: string; details?: ErrorDetail[] } } | null;
  if (!res.ok || !data?.file) throw new ApiError(res.status, data?.error?.code ?? "internal", data?.error?.message ?? `Erro ${res.status}`, data?.error?.details ?? []);
  return data.file;
}

/**
 * Opens (or downloads) a file. Downloads need the access token, which lives
 * only in memory, so the file is fetched and handed to the browser as a blob.
 */
export async function openFile(f: FileRef, download = false): Promise<void> {
  const go = () => {
    const headers: Record<string, string> = {};
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    const t = activeTenant();
    if (t) headers["x-varejoon-tenant"] = t;
    return fetch(`/v1/files/${f.id}${download ? "?download=1" : ""}`, { headers, credentials: "same-origin" });
  };
  let res = await go();
  if (res.status === 401 && (await refreshSession())) res = await go();
  if (!res.ok) throw new ApiError(res.status, "not_found", "Não foi possível abrir o arquivo");
  const url = URL.createObjectURL(await res.blob());
  const a = document.createElement("a");
  a.href = url;
  if (download || !res.headers.get("content-disposition")?.startsWith("inline")) a.download = f.name;
  else a.target = "_blank";
  a.rel = "noopener";
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 60_000);
}

export const get = <T>(path: string, opts?: { tenant?: boolean }) => api<T>("GET", path, undefined, opts);

/** `?a=1&b=2` from an object, skipping undefined; objects become JSON. */
export function qs(params: Record<string, unknown>): string {
  const p = new URLSearchParams();
  for (const [k, v] of Object.entries(params)) {
    if (v === undefined || v === null || v === "") continue;
    p.set(k, typeof v === "object" ? (Array.isArray(v) ? v.join(",") : JSON.stringify(v)) : String(v));
  }
  const s = p.toString();
  return s ? `?${s}` : "";
}
